export type ConnectionIssueKind = "offline" | "connection" | null;

// Note: resolves which (if any) connection banner to show. Offline takes
// precedence so only a single banner ever appears, even when SignalR is also
// down; a SignalR/live-connection problem is only relevant once authenticated.
export function resolveConnectionIssueKind(opts: {
  isOnline: boolean;
  isAuthenticated: boolean;
  isReconnecting: boolean;
  hasConnectionError: boolean;
  /** A live hub connection exists. Absence of an error is not the same thing. */
  hasLiveConnection?: boolean;
  /** A hub connection has been established at least once this session. */
  hasEverConnected?: boolean;
}): ConnectionIssueKind {
  if (!opts.isOnline) return "offline";
  if (!opts.isAuthenticated) return null;

  // Note: a connection that has gone away without reporting an error still has to raise the banner -
  // deriving health from the error alone meant any moment that cleared it read as recovered, and the
  // banner announced "Connection restored" then reported the failure again for as long as the
  // connection stayed down. It has to be a connection we once *had*, though: before the first attempt
  // there is legitimately no connection and nothing to warn about, and gating on "no attempt in
  // flight" was not the same test - isConnecting is false on the very first render too, so the banner
  // warned at boot and then announced a recovery a moment later when the hub connected normally.
  const lostConnection = opts.hasEverConnected === true && opts.hasLiveConnection === false;

  if (opts.isReconnecting || opts.hasConnectionError || lostConnection) return "connection";
  return null;
}

// Note: whether a warning already on screen may be replaced with "Connection restored". Closing it
// as soon as the issue *kind* cleared was not the same question: a fresh connect attempt clears the
// kind (see the isConnecting carve-out above) while nothing has actually recovered, so every retry
// announced a recovery and then reported the failure again - the "Connection restored / Connection
// lost" pair seen with the network held offline. Recovery means the network is back and, once
// authenticated, that a live hub connection exists.
export function hasRecovered(opts: {
  isOnline: boolean;
  isAuthenticated: boolean;
  hasLiveConnection: boolean;
}): boolean {
  if (!opts.isOnline) return false;
  return !opts.isAuthenticated || opts.hasLiveConnection;
}
